import {
  Button,
  MenuButton,
  MenuItem,
  useFeatureRequiredCallback,
} from 'components';
import { Theme } from '@app/theme';
import { createUseStyles } from 'react-jss';
import {
  API_URL,
  Feature,
  useCurrentOrganizationId,
  useGoToCalendarAuthUrl,
  useIsAdmin,
} from '@app/api';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  buttonIcon: {
    fontSize: theme.iconSize.regular,
    marginRight: theme.marginBase,
  },
  buttonContent: {
    display: 'flex',
    gap: theme.marginBase,
  },
  button: {
    minWidth: 'auto',
    whiteSpace: 'nowrap',
  },
}));

interface Props {
  children?: React.ReactNode;
}

export const AddCalendarAccountButton = ({ children }: Props) => {
  const classes = useStyles();
  const isAdmin = useIsAdmin();
  const { t } = useTranslation();

  const organizationId = useCurrentOrganizationId();
  const goToAuthUrlCallback = useGoToCalendarAuthUrl(organizationId);
  const goToAuthUrl = useFeatureRequiredCallback(
    Feature.CALENDAR_INTEGRATION,
    goToAuthUrlCallback,
  );

  const connectToGoogle = useFeatureRequiredCallback(
    Feature.CALENDAR_INTEGRATION,
    () => {
      // We use "window.open" to be able to close the window after the user has authorized the calendar
      // "window.close" can only be called on windows that were opened by a script
      window.open(`${API_URL}/integrations/google/connect`, '_blank');
    },
  );
  const connectToMicrosoft = useFeatureRequiredCallback(
    Feature.CALENDAR_INTEGRATION,
    () => {
      // We use "window.open" to be able to close the window after the user has authorized the calendar
      // "window.close" can only be called on windows that were opened by a script
      window.open(`${API_URL}/integrations/microsoft/connect`, '_blank');
    },
  );

  if (!isAdmin) {
    return (
      <Button
        onClick={goToAuthUrl}
        variant="muted"
        color="blue"
        className={classes.button}
        contentClassName={classes.buttonContent}
        text={t('editSession.settings.moderator.addCalendar')}
      />
    );
  }

  return (
    <MenuButton
      key="add"
      variant="muted"
      color="blue"
      contentClassName={classes.buttonContent}
      rightInsert=""
      textComponent={children}
    >
      <MenuItem
        onClick={connectToGoogle}
        label={t('settings.toolIntegrations.calendar.providers.google')}
      />
      <MenuItem
        onClick={connectToMicrosoft}
        label={t('settings.toolIntegrations.calendar.providers.microsoft')}
      />
      <MenuItem
        onClick={goToAuthUrl}
        label={t('settings.toolIntegrations.calendar.providers.cronofy')}
      />
    </MenuButton>
  );
};
